// Fichier : /src/pages/entreprise/cabinet-recrutement-marseille.tsx

import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/layout/layout';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import Button from '../../components/ui/button';
import HubSpotModal from '../../components/ui/hubspot-modal';
import SectionBackground from '../../components/sections/section-background';

const CabinetRecrutementMarseillePage = () => {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = React.useState(false);

  return (
    <Layout>
      <Helmet>
        <link rel="canonical" href="https://recruteurs.io/cabinet-recrutement-marseille/" />
      </Helmet>

      <WebPageSchema
        page={{
          title: "Cabinet de recrutement à Marseille | recruteurs.io",
          description:
            "Recruteurs.io, votre cabinet de recrutement à Marseille : expertise terrain, réseau candidat solide et méthode innovante basée sur l'IA et la performance.",
          url: "https://recruteurs.io/entreprise/cabinet-recrutement-marseille",
          lastReviewed: "2025-06-01",
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: "Accueil", url: "https://recruteurs.io" },
          { name: "Entreprise", url: "https://recruteurs.io/entreprise" },
          {
            name: "Cabinet de recrutement à Marseille",
            url: "https://recruteurs.io/entreprise/cabinet-recrutement-marseille",
          },
        ]}
      />

      <SectionBackground page="entreprise/cabinet-recrutement-marseille" section="hero">
        <section className="py-20 px-6 md:px-16 text-center min-h-[60vh] flex items-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
              Cabinet de recrutement à Marseille
            </h1>
            <p className="text-lg text-neutral-700 mb-6">
              Recruteurs.io accompagne les entreprises marseillaises depuis plus de 15 ans.
              Notre mission ? Vous faire gagner du temps avec une méthode performante, ancrée localement.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => setIsHubSpotModalOpen(true)}>Demander un devis</Button>
              <Button as="a" href="/reservation" variant="secondary">
                Planifier un appel
              </Button>
            </div>
          </div>
        </section>
      </SectionBackground>

      <section className="py-20 bg-gray-50 px-6 md:px-16">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Une équipe implantée à Marseille, à vos côtés</h2>
          <p className="text-lg text-neutral-700 mb-6">
            Notre équipe connaît le terrain, les spécificités du tissu économique local
            et les attentes concrètes des dirigeants dans les Bouches-du-Rhône.
          </p>
          <p className="text-lg text-neutral-700 mb-6">
            Présents à Marseille depuis plus de 15 ans, nous avons accompagné des centaines de candidats,
            dans tous les secteurs. Résultat : un <strong>réseau ultra qualifié</strong>, réactif et activable en quelques jours.
          </p>
          <ul className="list-disc text-left max-w-3xl mx-auto text-lg text-neutral-800 space-y-4 pl-6">
            <li>
              <strong>Connaissance fine des entreprises locales</strong> : culture, organisation, attentes
              managériales
            </li>
            <li>
              <strong>Recrutement de profils commerciaux, terrain, middle & top management</strong>
            </li>
            <li>
              <strong>Approche directe + outils IA</strong> pour sourcer rapidement les meilleurs talents du secteur
            </li>
          </ul>
        </div>
      </section>

      <section className="py-20 bg-white px-6 md:px-16 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">
            Un modèle <span className="text-gradient">basé sur la performance</span>
          </h2>
          <p className="text-lg text-neutral-700 mb-6">
            Pas de frais fixes injustifiés. Vous ne payez que si nous trouvons le bon candidat.
            Une approche transparente et orientée résultat.
          </p>
          <a
            href="/entreprise/recrutement"
            className="inline-block bg-gradient-to-r from-pink-500 to-orange-400 text-white px-6 py-3 rounded-full font-medium hover:scale-105 transition"
          >
            Voir notre méthode complète
          </a>
        </div>
      </section>

      <section className="bg-white py-20 px-6 md:px-16">
        <div className="max-w-5xl mx-auto text-lg text-neutral-800 leading-relaxed">
          <h2 className="text-3xl font-bold mb-6 text-gradient">
            Pourquoi faire appel à un cabinet de recrutement à Marseille ?
          </h2>
          <p className="mb-4">
            Le marché de l'emploi dans les Bouches-du-Rhône évolue rapidement, porté par un tissu économique dynamique entre industries, services et innovation. À Marseille, recruter efficacement demande une approche à la fois humaine, technologique et locale.
          </p>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Une implantation locale, un réseau puissant</h3>
          <p className="mb-4">
            Depuis plus de 15 ans, notre cabinet de recrutement basé à Marseille accompagne dirigeants, DRH et fondateurs dans leurs recrutements stratégiques. Cette présence durable nous permet de mobiliser rapidement un <strong>réseau de candidats locaux</strong> — souvent invisibles sur les jobboards traditionnels.
          </p>
          <p className="mb-4">
            Notre expertise s'étend à des profils pénuriques : commerciaux terrain, managers opérationnels, fonctions support et profils tech. Grâce à une approche directe et une connaissance fine des entreprises marseillaises, nous identifions les meilleurs profils… avant vos concurrents.
          </p>
          <h3 className="text-2xl font-semibold mt-8 mb-4">Notre méthode fait la différence</h3>
          <p className="mb-4">
            Chaque recrutement est pensé comme un funnel : <strong>attraction, sélection, transformation</strong>. Nous combinons sourcing sur-mesure, IA et entretiens structurés pour garantir des embauches durables. Notre <a href="/entreprise/recrutement" className="underline">méthode RH</a> réduit les risques de turnover et valorise votre culture d'entreprise.
          </p>
          <p className="mb-4">
            En complément, notre outil <a href="https://pimpmyjob.fr" className="underline">PimpMyJob</a> vous permet de générer des offres d'emploi professionnelles et engageantes en quelques clics.
          </p>
         <h3 className="text-2xl font-semibold mt-8 mb-4">Une tarification transparente</h3>
<p className="mb-4">
  Contrairement à de nombreux cabinets, nous ne facturons pas à l'étape du CV transmis. Notre <a href="/infos/tarifs" className="underline">modèle à la performance</a> valorise les résultats réels : présentation, embauche, maintien dans le poste. Pas de surprise, pas de frais cachés.
</p>

{/* 📺 Nouvelle section vidéo */}
<h3 className="text-2xl font-semibold mt-12 mb-4">Recruteurs.io passe à la télé</h3>
<p className="mb-4">
  Découvrez en images notre approche, notre équipe, et ce qui rend notre cabinet de recrutement unique sur le terrain.
</p>
<div className="w-full max-w-4xl mx-auto mb-8" style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
  <iframe
    src="https://www.youtube.com/embed/VIDÉO_ID"
    title="Recruteurs.io à la télé"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
    allowFullScreen
    className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
  ></iframe>
</div>

<h3 className="text-2xl font-semibold mt-8 mb-4">Vous cherchez un partenaire à Marseille ?</h3>
          <p className="mb-4">
            Que vous recrutiez dans le 13, sur Aix-en-Provence, Salon ou Vitrolles, notre cabinet de recrutement vous accompagne avec une méthodologie éprouvée, personnalisée et efficace.
          </p>
          <p>
            <strong>Recruteurs.io</strong> est plus qu'un cabinet, c'est un partenaire RH local, agile, aligné sur vos enjeux de croissance.
          </p>
        </div>
      </section>

      <HubSpotModal isOpen={isHubSpotModalOpen} onClose={() => setIsHubSpotModalOpen(false)} />
    </Layout>
  );
};

export default CabinetRecrutementMarseillePage;